import type { TraceEvent } from '../../api';
import { formatMs } from '../../format';
import {
  fallbackEventText,
  parseJSONLike,
  type ParsedTracePayload,
} from '../../trace/parse-payload';
import { JsonBlock } from '../raw/raw-tab';

function objectValue(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function EventDetailContent({
  event,
  parsed,
}: {
  event: TraceEvent;
  parsed: ParsedTracePayload;
}) {
  switch (parsed.kind) {
    case 'assistant_tool_calls':
      return <AssistantToolCallCard parsed={parsed} />;
    case 'tool_call':
      return <ToolCallCard parsed={parsed} event={event} />;
    case 'user_message':
      return <UserMessageCard parsed={parsed} />;
    case 'tool_result_message':
      return <ToolResultCard parsed={parsed} />;
    case 'llm_call':
      return <LLMCallCard parsed={parsed} event={event} />;
    case 'billing_router':
      return <BillingRouterCard parsed={parsed} />;
    case 'runtime_guidance':
      return <RuntimeGuidanceCard parsed={parsed} />;
    case 'assistant_text':
      return <GenericTextCard title="Assistant text" text={parsed.text} />;
    case 'generic_json':
      return <GenericJsonCard value={parsed.value} />;
    case 'generic_text':
      return <GenericTextCard title="Content" text={parsed.text} />;
  }
}

function AssistantToolCallCard({
  parsed,
}: {
  parsed: Extract<ParsedTracePayload, { kind: 'assistant_tool_calls' }>;
}) {
  return (
    <div className="space-y-3">
      {parsed.text ? (
        <PayloadSection title="Assistant text" value={parsed.text} />
      ) : null}
      {parsed.toolCalls.map((toolCall, index) => (
        <div key={toolCall.id || index} className="rounded-md border">
          <div className="border-b px-3 py-2 text-sm font-medium">
            {toolCall.name}
          </div>
          <div className="space-y-3 p-3">
            <DetailPill label="Tool call id" value={toolCall.id} />
            <DetailPill
              label="Activity goal"
              value={objectValue(toolCall.arguments)?.activity_goal}
            />
            {toolCall.name === 'send_message' &&
            objectValue(toolCall.arguments)?.content ? (
              <PayloadSection
                title="Content blocks"
                value={objectValue(toolCall.arguments)?.content}
              />
            ) : null}
            <PayloadSection title="Arguments" value={toolCall.arguments} />
            <details className="rounded-md border p-3 text-sm">
              <summary className="cursor-pointer text-muted-foreground">
                Raw tool call
              </summary>
              <div className="mt-3">
                <JsonBlock value={toolCall.raw} />
              </div>
            </details>
          </div>
        </div>
      ))}
    </div>
  );
}

function ToolCallCard({
  parsed,
  event,
}: {
  parsed: Extract<ParsedTracePayload, { kind: 'tool_call' }>;
  event: TraceEvent;
}) {
  const sections = toolSpecificSections(parsed);
  return (
    <div className="space-y-3">
      <div className="rounded-md border">
        <div className="border-b px-3 py-2 text-sm font-medium">
          {parsed.toolName || event.title || 'Tool call'}
        </div>
        <div className="space-y-3 p-3">
          <DetailPill label="Tool call id" value={parsed.toolCallId} />
          {sections.pills.map(pill => (
            <DetailPill
              key={pill.label}
              label={pill.label}
              value={pill.value}
            />
          ))}
          {sections.payloads.map(section => (
            <PayloadSection
              key={section.title}
              title={section.title}
              value={section.value}
            />
          ))}
          {parsed.input !== undefined && parsed.input !== '' ? (
            <PayloadSection title="Raw input" value={parsed.input} />
          ) : null}
          {parsed.output !== undefined && parsed.output !== '' ? (
            <PayloadSection title="Raw output" value={parsed.output} />
          ) : null}
          {parsed.error ? (
            <PayloadSection title="Error" value={parsed.error} destructive />
          ) : null}
          {parsed.input === undefined &&
          parsed.output === undefined &&
          !parsed.error ? (
            <TextContentBlock
              title="Summary"
              value={fallbackEventText(event)}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function toolSpecificSections(
  parsed: Extract<ParsedTracePayload, { kind: 'tool_call' }>
) {
  const input = objectValue(parsed.input);
  const output = objectValue(parsed.output);
  const toolName = parsed.toolName.toLowerCase();
  const pills: { label: string; value: unknown }[] = [];
  const payloads: { title: string; value: unknown }[] = [];

  if (toolName === 'list' || toolName === 'glob') {
    pills.push(
      { label: 'Path', value: input?.path || input?.directory },
      { label: 'Pattern', value: input?.pattern || input?.glob },
      { label: 'Recursive', value: input?.recursive }
    );
    payloads.push({
      title: 'Entries',
      value: output?.entries || output?.files || output?.result,
    });
  } else if (toolName === 'read') {
    pills.push({ label: 'Path', value: input?.file_path || input?.path });
    payloads.push({
      title: 'Content preview',
      value: output?.content || output?.text || output?.result,
    });
  } else if (toolName === 'search_conversations' || toolName === 'recall') {
    pills.push(
      { label: 'Query', value: input?.query },
      { label: 'Mode', value: input?.mode || input?.scope }
    );
    payloads.push({
      title: 'Top results',
      value: output?.results || output?.memories || output?.result,
    });
  } else if (toolName === 'manage_schedule') {
    pills.push(
      { label: 'Action', value: input?.action },
      { label: 'Schedule', value: input?.schedule_id || input?.title }
    );
    payloads.push({ title: 'Result', value: parsed.output });
  } else if (toolName === 'exec' || toolName === 'javascript') {
    payloads.push(
      {
        title: toolName === 'exec' ? 'Command' : 'Code',
        value: input?.command || input?.code,
      },
      { title: 'Stdout', value: output?.stdout },
      { title: 'Stderr', value: output?.stderr },
      { title: 'Exit / error', value: output?.exit_code ?? output?.error }
    );
  }

  return {
    pills: pills.filter(pill => pill.value !== undefined && pill.value !== ''),
    payloads: payloads.filter(
      section => section.value !== undefined && section.value !== ''
    ),
  };
}

function UserMessageCard({
  parsed,
}: {
  parsed: Extract<ParsedTracePayload, { kind: 'user_message' }>;
}) {
  const userText = (parsed.text || '').trim();
  const runtimeBlocks = parsed.runtimeContext || [];
  const authoredText = runtimeBlocks.reduce(
    (current, block) => current.replace(block, '').trim(),
    userText
  );
  return (
    <div className="space-y-3">
      {runtimeBlocks.length ? (
        <PayloadSection title="Runtime context" value={runtimeBlocks} />
      ) : null}
      <PayloadSection
        title="User content"
        value={
          authoredText || (runtimeBlocks.length ? '-' : parsed.content || '-')
        }
      />
    </div>
  );
}

function ToolResultCard({
  parsed,
}: {
  parsed: Extract<ParsedTracePayload, { kind: 'tool_result_message' }>;
}) {
  return (
    <div className="space-y-3">
      <DetailPill label="Tool call id" value={parsed.toolCallId} />
      <DetailPill
        label="Pairing"
        value={parsed.paired ? 'paired mirror' : 'unpaired'}
      />
      <PayloadSection title="Result" value={parsed.content || '-'} />
    </div>
  );
}

function LLMCallCard({
  parsed,
  event,
}: {
  parsed: Extract<ParsedTracePayload, { kind: 'llm_call' }>;
  event: TraceEvent;
}) {
  const details = event.details || {};
  return (
    <div className="space-y-3">
      <div className="grid gap-2 text-sm sm:grid-cols-2">
        <DetailPill
          label="Provider"
          value={parsed.provider || details.provider}
        />
        <DetailPill label="Model" value={parsed.model || details.model} />
        <DetailPill
          label="Finish reason"
          value={parsed.finishReason || details.finish_reason}
        />
        <DetailPill label="Status" value={event.status} />
        <DetailPill
          label="TTFT"
          value={formatMs(numberDetail(details.ttft_ms))}
        />
        <DetailPill label="Duration" value={formatMs(event.duration_ms)} />
        <DetailPill label="Input tokens" value={details.input_tokens} />
        <DetailPill label="Output tokens" value={details.output_tokens} />
      </div>
      <PayloadSection
        title="Payload"
        value={parsed.raw || fallbackEventText(event)}
      />
    </div>
  );
}

function BillingRouterCard({
  parsed,
}: {
  parsed: Extract<ParsedTracePayload, { kind: 'billing_router' }>;
}) {
  return (
    <div className="space-y-3">
      <DetailPill label="Route marker" value={parsed.route || 'tool_router'} />
      <PayloadSection title="Payload" value={parsed.raw} />
    </div>
  );
}

function RuntimeGuidanceCard({
  parsed,
}: {
  parsed: Extract<ParsedTracePayload, { kind: 'runtime_guidance' }>;
}) {
  return <PayloadSection title="Runtime guidance" value={parsed.blocks} />;
}

function GenericJsonCard({ value }: { value: unknown }) {
  return <PayloadSection title="Payload" value={value} />;
}

function GenericTextCard({ title, text }: { title: string; text: string }) {
  return <TextContentBlock title={title} value={text} />;
}

function PayloadSection({
  title,
  value,
  destructive,
}: {
  title: string;
  value: unknown;
  destructive?: boolean;
}) {
  const parsed = parseJSONLike(value);
  return (
    <div className="space-y-1.5">
      <div
        className={`text-xs font-medium uppercase tracking-normal ${
          destructive ? 'text-destructive' : 'text-muted-foreground'
        }`}
      >
        {title}
      </div>
      {parsed && typeof parsed === 'object' ? (
        <JsonBlock value={parsed} />
      ) : (
        <TextContent value={String(parsed || '-')} destructive={destructive} />
      )}
    </div>
  );
}

function TextContentBlock({ title, value }: { title: string; value?: string }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium uppercase tracking-normal text-muted-foreground">
        {title}
      </div>
      <TextContent value={value || '-'} />
    </div>
  );
}

function TextContent({
  value,
  destructive,
}: {
  value: string;
  destructive?: boolean;
}) {
  return (
    <div
      className={`max-h-[560px] overflow-auto whitespace-pre-wrap break-words rounded-md border p-3 text-sm ${
        destructive ? 'border-destructive/40 text-destructive' : 'bg-muted/30'
      }`}
    >
      {value}
    </div>
  );
}

function DetailPill({ label, value }: { label: string; value: unknown }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="rounded-md border px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="break-words text-sm font-medium">{String(value)}</div>
    </div>
  );
}

function numberDetail(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
